import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfigService } from 'src/services/config.service';
import { HttpClientWrapperService } from 'src/services/http-client-wrapper.service';

export type DialogStatus = 'processing' | 'completed' | 'failed';

@Component({
  selector: 'app-download-progress',
  templateUrl: './download-progress.component.html',
  styleUrls: ['./download-progress.component.scss']
})
export class DownloadProgressComponent implements OnInit, OnDestroy {

  @Input() jobId: string = '';
  @Input() fileName: string = `report-${Date.now()}.xlsx`;
  @Input() sseUrl: string = 'api/reports/stream';
  @Input() downloadUrl: string = 'api/reports/download';

  @Output() closed = new EventEmitter<void>();

  status: DialogStatus = 'processing';
  progress: number = 0;
  statusMessage: string = 'Preparing your report…';
  errorMessage: string = '';

  private abortController: AbortController | null = null;

  constructor(
    private http: HttpClientWrapperService,
    private config: ConfigService
  ) { }

  ngOnInit(): void {
    setTimeout(() => {
      this.connectSSE();
    }, 3000);
  }

  private getToken(): string {
    // Adjust this to wherever your app stores the auth token
    return localStorage.getItem('token') || '';
  }

  private connectSSE(): void {
    const url = `${this.config.json.apiEndpoint}${this.sseUrl}/${this.jobId}`;
    const token = this.getToken();

    this.abortController = new AbortController();

    fetch(url, {
      method: 'GET',
      signal: this.abortController.signal,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`SSE connection failed with status ${response.status}`);
        }
        if (!response.body) {
          throw new Error('ReadableStream not supported in this browser.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const read = (): void => {
          reader.read().then(({ done, value }) => {
            if (done) return;

            buffer += decoder.decode(value, { stream: true });

            // SSE messages are separated by double newlines
            const parts = buffer.split('\n\n');
            // Last part may be incomplete — keep it in buffer
            buffer = parts.pop() || '';

            parts.forEach(part => {
              const dataLine = part.split('\n').find(line => line.startsWith('data:'));
              if (dataLine) {
                const raw = dataLine.replace(/^data:\s*/, '').trim();
                if (raw) this.handleEvent(raw);
              }
            });

            read(); // continue reading
          }).catch(err => {
            // AbortError is expected when we cancel — ignore it
            if (err?.name !== 'AbortError' && this.status !== 'completed') {
              this.status = 'failed';
              this.statusMessage = 'Connection lost. Please try again.';
              this.errorMessage = 'The server connection was interrupted.';
            }
          });
        };

        read();
      })
      .catch(err => {
        if (err?.name !== 'AbortError' && this.status !== 'completed') {
          this.status = 'failed';
          this.statusMessage = 'Connection lost. Please try again.';
          this.errorMessage = err?.message || 'Failed to connect to the server.';
        }
      });
  }

  private handleEvent(raw: string): void {
    try {
      const payload = JSON.parse(raw);
      const received: number = payload.progress ?? payload.percent ?? 0;
      this.progress = Math.min(100, Math.max(0, received));
      this.statusMessage = payload.message || this.getDefaultMessage(this.progress);

      if (this.progress >= 100) {
        this.progress = 100;
        this.status = 'completed';
        this.statusMessage = 'Report ready! Starting download…';
        this.closeSSE();
        setTimeout(() => this.triggerDownload(), 600);
      }
    } catch (e) {
      console.error('SSE parse error:', e);
    }
  }

  private getDefaultMessage(pct: number): string {
    if (pct < 20) return 'Fetching subscriber data…';
    if (pct < 45) return 'Processing records…';
    if (pct < 70) return 'Formatting Excel sheets…';
    if (pct < 90) return 'Finalising the report…';
    if (pct < 100) return 'Almost done…';
    return 'Report ready!';
  }

  private triggerDownload(): void {
    this.http.get(`${this.config.json.apiEndpoint}${this.downloadUrl}/${this.jobId}`, {
      responseType: 'blob',
      observe: 'response'   // ← get full response including headers
    })
      .subscribe({
        next: (response: any) => {
          const blob: Blob = response.body;

          // Extract filename from Content-Disposition header
          // Backend should send: Content-Disposition: attachment; filename="subscribers-report.xlsx"
          const contentDisposition = response.headers.get('Content-Disposition') || '';
          const match = contentDisposition.match(/filename[^;=\n]*=\s*["']?([^"';\n]+)["']?/);
          const fileName = match?.[1]?.trim() || this.fileName; // fallback to @Input fileName

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;   // ← uses backend filename
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setTimeout(() => this.close(), 1200);
        },
        error: (err) => {
          this.status = 'failed';
          this.errorMessage = err?.error?.message || 'Download failed. Please try again.';
          this.statusMessage = 'Download failed.';
        }
      });
  }

  private closeSSE(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  close(): void {
    this.closeSSE();
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.closeSSE();
  }
}